// The page side of a press on a push notification (player-reach.md §3.5,
// R1301).
//
// The service worker (`client/public/sw.js`) answers a press in one of two
// ways, and this module reads both:
//
//   ?world=<world>&cell=<cell>
//       a new window. The entry joins the world as the `?world=` deep link
//       does (`fast/entry.ts`), and the camera goes to the cell after the
//       first Empire frame, when the map of the seat is there.
//   {type: 'aqua-push-open', world, cell}
//       a message to a game tab that is already in the world. The camera
//       goes to the cell at once, and no second tab joins the seat.
//
// The cell parameter is taken off the address at once, as the replay link
// is: a reload must not move the camera again.

import type { Handlers } from '../net/connection';

/** The query key of the cell a push notification names. */
export const CELL_KEY = 'cell';

/** The type of the message the service worker posts to an open game tab.
 *  `sw.js` spells the same word. */
export const PUSH_OPEN = 'aqua-push-open';

/** One cell in one world. */
export interface CellLink {
  world: number;
  cell: number;
}

/** A positive whole number, or 0 for anything else. */
function positive(raw: string): number {
  if (!/^[0-9]{1,15}$/.test(raw)) return 0;
  const n = Number(raw);
  return n > 0 ? n : 0;
}

/** The link of a world and a cell, or undefined where either one is 0. */
function linkOf(world: number, cell: number): CellLink | undefined {
  return world > 0 && cell > 0 ? { world, cell } : undefined;
}

/** Reads the cell link off a page address, or undefined when the address
 *  names no world or no cell. */
export function readCellLink(params: URLSearchParams): CellLink | undefined {
  return linkOf(positive(params.get('world') ?? ''), positive(params.get(CELL_KEY) ?? ''));
}

/** Takes the cell parameter off the address bar. The world stays, because
 *  a reload must land in the same world (`rememberWorld`). */
export function forgetCellLink(): void {
  const url = new URL(location.href);
  if (!url.searchParams.has(CELL_KEY)) return;
  url.searchParams.delete(CELL_KEY);
  history.replaceState(null, '', url.toString());
}

/** Reads one message of the service worker, or undefined when it is not
 *  the open message or names no cell. */
export function readOpenMessage(data: unknown): CellLink | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const m = data as { type?: unknown; world?: unknown; cell?: unknown };
  if (m.type !== PUSH_OPEN) return undefined;
  const whole = (v: unknown): number =>
    (typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : 0);
  return linkOf(whole(m.world), whole(m.cell));
}

/**
 * Wraps the Empire handler of the feed so the first frame moves the
 * camera to `cell`. The link acts one time: a reconnect does not move the
 * camera again.
 */
export function armCellLink(h: Handlers, cell: number, focus: (cell: number) => void): void {
  let done = false;
  const onEmpire = h.onEmpire.bind(h);
  h.onEmpire = (e) => {
    onEmpire(e);
    if (done) return;
    done = true;
    focus(cell);
  };
}

/** The part of `ServiceWorkerContainer` this module listens on. */
export interface MessageSource {
  addEventListener(type: 'message', fn: (ev: MessageEvent) => void): void;
  startMessages?(): void;
}

/**
 * Listens for the open message of the service worker. `world` reads the
 * world the page is in now, because a page can change its world. A
 * message for another world does nothing: the worker picks a tab by the
 * world in its address, and a stale address must not move the camera of
 * this world.
 */
export function listenPushOpen(
  world: () => number, focus: (cell: number) => void,
  source: MessageSource | undefined = globalThis.navigator?.serviceWorker,
): void {
  if (!source) return;
  source.addEventListener('message', (ev) => {
    const link = readOpenMessage(ev.data);
    if (link && world() > 0 && link.world === world()) focus(link.cell);
  });
  // A listener added with addEventListener opens the queue only when the
  // document has loaded. The call opens it now.
  source.startMessages?.();
}
